import { Paper, Typography, Box } from '@mui/material';
import dayjs from 'dayjs';
import './CircadianChartToolTip.scss';
import { useGeneralSettings } from '../../../../../hooks';
import { getCssVariable } from '../../../../../utils';
import { CircadianChartDecorator } from '../types';
import { useTranslation } from 'react-i18next';
import { getFluctuationReasonLabel } from '../../../i18n';

interface CircadianChartToolTipProps {
  active?: boolean;
  payload?: any[];
  label?: number | string;
  hoveredDecorator?: CircadianChartDecorator | null;
  decorators?: CircadianChartDecorator[];
}

const DECORATOR_MATCH_TOLERANCE_MS = 60 * 1000;

const getCandidateTime = (payloadTime: unknown, label: number | string | undefined) => {
  if (typeof payloadTime === 'number') return payloadTime;
  if (typeof label === 'number') return label;
  if (typeof label === 'string') {
    const parsed = dayjs(label);
    if (parsed.isValid()) return parsed.valueOf();
  }
  return undefined;
};

const formatFluctuationValue = (value: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  const sign = value > 0 ? '+' : '-';
  const absValue = Math.abs(value);

  if (absValue === 0) return '0';

  // Avoid showing tiny non-zero values as 0 because of rounding.
  if (absValue < 0.1) {
    return `${sign}${absValue.toFixed(2)}`;
  }

  return `${sign}${(Math.round(absValue * 10) / 10).toFixed(1)}`;
};

const CircadianChartToolTip = ({
  active,
  payload,
  label,
  hoveredDecorator,
  decorators,
}: CircadianChartToolTipProps) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const neutralFluctuationColor = getCssVariable('--color-black');
  if (!active) return null;

  const normalizedPayload = payload ?? [];
  const currentValue = normalizedPayload.find((p) => p.dataKey === 'currentValue')?.value;
  const avgValue = normalizedPayload.find((p) => p.dataKey === 'avgValue')?.value;
  const time = normalizedPayload[0]?.payload?.time;
  const is24h = timeFormat === '24h';
  const candidateTime = getCandidateTime(time, label);
  const matchedDecorators =
    hoveredDecorator?.details && hoveredDecorator.time !== undefined
      ? [hoveredDecorator]
      : candidateTime !== undefined && decorators?.length
        ? decorators.filter((decorator) => {
            if (!decorator.details) return false;
            if (decorator.time === candidateTime) return true;
            return Math.abs(decorator.time - candidateTime) <= DECORATOR_MATCH_TOLERANCE_MS;
          })
        : [];

  const hasCurrentValue = currentValue !== undefined && currentValue !== null;
  const hasAvgValue = avgValue !== undefined && avgValue !== null && !Number.isNaN(avgValue);

  const hasDecoratorDetails = matchedDecorators.length > 0;
  const hasChartValues = hasCurrentValue || hasAvgValue;

  if (!hasDecoratorDetails && !hasChartValues && candidateTime === undefined) {
    return null;
  }

  const timeLabel =
    hoveredDecorator?.time !== undefined
      ? dayjs(hoveredDecorator.time).format(is24h ? 'HH:mm' : 'h:mm a')
      : matchedDecorators[0]?.time !== undefined
        ? dayjs(matchedDecorators[0].time).format(is24h ? 'HH:mm' : 'h:mm a')
      : candidateTime !== undefined
        ? dayjs(candidateTime).format(is24h ? 'HH:mm' : 'h:mm a')
        : time
          ? dayjs(time).format(is24h ? 'HH:mm' : 'h:mm a')
          : '';

  const shouldShowChartMetrics = !hasDecoratorDetails;

  return (
    <Paper className="chart-tooltip" elevation={0}>
      {timeLabel && (
        <Box className="chart-tooltip__header">
          <Typography variant="caption" className="chart-tooltip__header-label">
            {t('data.circadian.tooltip.time')}
          </Typography>
          <Typography variant="body2" className="chart-tooltip__header-time">
            {timeLabel}
          </Typography>
        </Box>
      )}
      {shouldShowChartMetrics && (
        <Box className="chart-tooltip__metrics">
          {hasCurrentValue && (
            <Box className="chart-tooltip__metric">
              <span className="chart-tooltip__dot chart-tooltip__dot--current" />
              <Typography variant="caption" className="chart-tooltip__label">
                {t('data.circadian.tooltip.actual')}
              </Typography>
              <Typography variant="body2" className="chart-tooltip__value">
                {(currentValue * 100).toFixed(0)}
              </Typography>
            </Box>
          )}
          {hasAvgValue && (
            <Box className="chart-tooltip__metric">
              <span className="chart-tooltip__dot chart-tooltip__dot--avg" />
              <Typography variant="caption" className="chart-tooltip__label">
                {t('data.circadian.tooltip.average')}
              </Typography>
              <Typography variant="body2" className="chart-tooltip__value">
                {((avgValue as number) * 100).toFixed(0)}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {matchedDecorators.map((decorator, index) => {
        const details = decorator.details!;
        const fluctuationValue = formatFluctuationValue(details.fluctuateValue);
        const numericFluctuation =
          typeof details.fluctuateValue === 'number' ? details.fluctuateValue : null;
        const absoluteFluctuation =
          numericFluctuation !== null ? Math.abs(numericFluctuation) : null;
        const fluctuationColor =
          numericFluctuation === null || absoluteFluctuation === 0
            ? neutralFluctuationColor
            : numericFluctuation > 0
              ? 'success.main'
              : 'error.main';
        const fluctuationTone =
          numericFluctuation === null || absoluteFluctuation === 0
            ? 'neutral'
            : numericFluctuation > 0
              ? 'positive'
              : 'negative';

        return (
          <Box key={`${decorator.time}-${index}`} className="chart-tooltip__event">
            <Box className="chart-tooltip__item">
              <Typography variant="caption" className="chart-tooltip__label">
                {t('data.circadian.tooltip.event')}
              </Typography>
              <Typography variant="body2" className="chart-tooltip__value">
                {details.title}
              </Typography>
            </Box>
            <Box className="chart-tooltip__item">
              <Typography variant="caption" className="chart-tooltip__label">
                {t('data.circadian.tooltip.fluctuation')}
              </Typography>
              <Typography
                variant="body2"
                className={`chart-tooltip__fluctuation chart-tooltip__fluctuation--${fluctuationTone}`}
                sx={{ color: fluctuationColor }}
              >
                {fluctuationValue}
              </Typography>
            </Box>
            <Box className="chart-tooltip__item chart-tooltip__item--last">
              <Typography variant="caption" className="chart-tooltip__label">
                {t('data.circadian.tooltip.source')}
              </Typography>
              <Typography
                variant="body2"
                className="chart-tooltip__value"
              >
                {getFluctuationReasonLabel(t, details.reason)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

export default CircadianChartToolTip;
